"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.__resetAuthForTests = exports.getToken = exports.useAuth = exports.AuthProvider = exports.authReady = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const keycloak_js_1 = __importDefault(require("keycloak-js"));
const runtime_config_1 = require("../../../common/config/runtime-config");
const LoadingScreen_1 = require("../../../components/loader/LoadingScreen");
const tokenStore_1 = require("../tokenStore");
const AuthContext = (0, react_1.createContext)(undefined);
let keycloakInstance = null;
let initPromise = null;
let authChannel = new BroadcastChannel('auth_channel');
let authReadyResolve = null;
const getCleanRedirectUri = () => window.location.origin + window.location.pathname;
exports.authReady = new Promise((resolve) => {
    authReadyResolve = resolve;
});
const resolveAuthReady = () => {
    if (authReadyResolve) {
        authReadyResolve();
        authReadyResolve = null;
    }
};
const AuthProvider = ({ children, }) => {
    const [isLoading, setIsLoading] = (0, react_1.useState)(true);
    const [isLoggedIn, setLoggedIn] = (0, react_1.useState)(false);
    const [userInfo, setUserInfo] = (0, react_1.useState)(null);
    const [roles, setRoles] = (0, react_1.useState)([]);
    const providerId = (0, react_1.useRef)(crypto.randomUUID()).current;
    const config = (0, runtime_config_1.getConfig)();
    // 2. Envolver handleLocalLogout en useCallback para estabilizar su referencia.
    const handleLocalLogout = (0, react_1.useCallback)(() => {
        setUserInfo(null);
        setLoggedIn(false);
        (0, tokenStore_1.setGlobalToken)(null);
        localStorage.clear();
    }, []); // Las dependencias están vacías porque los setters de estado son estables.
    const updateUserInfo = async () => {
        if (!keycloakInstance?.authenticated) {
            handleLocalLogout();
            return;
        }
        try {
            await keycloakInstance.updateToken(30);
            (0, tokenStore_1.setGlobalToken)(keycloakInstance.token ?? null);
            const profile = await keycloakInstance.loadUserProfile();
            setUserInfo({
                username: `${profile.firstName} ${profile.lastName}`,
                email: profile.email,
                tenant: Array.isArray(keycloakInstance.tokenParsed?.tenant_name)
                    ? keycloakInstance.tokenParsed.tenant_name[0].replace(/^\//, '')
                    : keycloakInstance.tokenParsed?.tenant_name?.replace(/^\//, ''),
            });
            setLoggedIn(true);
        }
        catch (error) {
            console.error('Failed to load user profile', error);
            handleLocalLogout();
        }
        const parsed = keycloakInstance.tokenParsed;
        setRoles(parsed?.realm_access?.roles ?? []);
    };
    const hasRole = (0, react_1.useCallback)((role) => roles.includes(role), [roles]);
    const hasPermission = (permission) => keycloakInstance?.tokenParsed?.resource_access?.['smart-vending-web']?.roles?.includes(permission) ?? false;
    const logout = () => {
        handleLocalLogout();
        authChannel.postMessage({ type: 'LOGOUT', sender: providerId });
        keycloakInstance?.logout({
            redirectUri: window.location.origin,
        });
    };
    (0, react_1.useEffect)(() => {
        const boot = async () => {
            try {
                if (!keycloakInstance) {
                    keycloakInstance = new keycloak_js_1.default({
                        url: config.KEYCLOAK_URL,
                        realm: config.KEYCLOAK_REALM,
                        clientId: config.KEYCLOAK_CLIENT_ID,
                    });
                    initPromise = keycloakInstance.init({
                        onLoad: 'login-required',
                        pkceMethod: 'S256',
                        redirectUri: getCleanRedirectUri(),
                        checkLoginIframe: false,
                    });
                }
                await initPromise;
                await updateUserInfo(); // ⬅️ ESPERAR SIEMPRE
            }
            catch (e) {
                console.error('Auth boot failed', e);
                handleLocalLogout();
            }
            finally {
                resolveAuthReady();
                setIsLoading(false); // ⬅️ SOLO CUANDO TODO ESTÁ LISTO
            }
        };
        boot();
    }, []);
    // Refresco automático de token
    (0, react_1.useEffect)(() => {
        if (!keycloakInstance)
            return;
        const interval = setInterval(() => {
            keycloakInstance?.updateToken(30).catch(() => {
                console.warn('Token expirado, cerrando sesión…');
                logout();
            });
        }, 20_000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]); // Depende de isLoggedIn para iniciar/detener
    // Comunicación entre pestañas
    (0, react_1.useEffect)(() => {
        authChannel.onmessage = (event) => {
            const message = event.data;
            if (message.sender === providerId)
                return;
            const currentUser = keycloakInstance?.tokenParsed?.preferred_username;
            if (message.type === 'LOGOUT')
                handleLocalLogout();
            if (message.type === 'LOGIN' &&
                message.user &&
                message.user !== currentUser) {
                keycloakInstance?.updateToken(30).catch(() => window.location.reload());
            }
        };
        return () => {
            authChannel.onmessage = null;
        };
        // 3. Añadir handleLocalLogout al array de dependencias.
    }, [isLoggedIn, providerId, handleLocalLogout]);
    if (isLoading)
        return (0, jsx_runtime_1.jsx)(LoadingScreen_1.LoadingScreen, {});
    return ((0, jsx_runtime_1.jsx)(AuthContext.Provider, { value: {
            isLoggedIn,
            userInfo,
            keycloak: keycloakInstance,
            hasRole,
            hasPermission,
            getToken: exports.getToken,
            isLoading,
            logout,
        }, children: children }));
};
exports.AuthProvider = AuthProvider;
const useAuth = () => {
    const context = (0, react_1.useContext)(AuthContext);
    if (!context)
        throw new Error('useAuth debe usarse dentro de un AuthProvider');
    return context;
};
exports.useAuth = useAuth;
const getToken = async () => {
    const isInvoiceRoute = window.location.pathname.startsWith('/app/invoices/');
    if (isInvoiceRoute) {
        return null;
    }
    await exports.authReady;
    if (!keycloakInstance) {
        return null;
    }
    try {
        await keycloakInstance.updateToken(30);
        const token = keycloakInstance.token ?? null;
        if (!token) {
            console.warn('No token, redirigiendo a login');
            await keycloakInstance.login({ redirectUri: getCleanRedirectUri() });
        }
        return token;
    }
    catch (error) {
        console.error('Error actualizando el token', error);
        await keycloakInstance.login({ redirectUri: getCleanRedirectUri() });
        return null;
    }
};
exports.getToken = getToken;
const __resetAuthForTests = () => {
    keycloakInstance = null;
    initPromise = null;
    if (authChannel) {
        authChannel.onmessage = null;
        authChannel.close();
    }
    authChannel = new BroadcastChannel('auth_channel');
};
exports.__resetAuthForTests = __resetAuthForTests;
